import React, {useEffect, useState} from "react";

import {Box} from '@mui/material';

import "../App.css"
import {PageHeader} from "./PageHeader";
import {useAppCtx} from "../app/AppCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {accessCtrl} from "../ctx/AccessCtrl";
import {CxGrid} from "../cmx/CxGrid";
import {
    CxTextField,
    CxDatePicker,
    CxCancelSaveButton,
    CxDeleteCancelSaveButton,
    CxComboxSelection,
    CxLocalization,
    CxMetaInfo, CxCheckBox
} from "../cmx/CxForm";
import {CxConfirmDialog} from "../cmx/CxDialog";
import {CxFormFeedback} from "../cmx/CxFeedback";
import {Project} from "../domain/Project"
import {initialCall, runningCall} from "../gateway/CallService";
import {projectSave, projectDelete} from "../gateway/ProjectService";
import {DateUtil} from "../lib/DateUtil"
import {ValidationUtil} from "../lib/ValidationUtil";
import {i18n} from "../ctx/I18n";

// --------------------------------------------------------------------------------------------------------------------
// RenderProject
// --------------------------------------------------------------------------------------------------------------------

const RenderProject = (currentData=null) => {
    const isUpdate = currentData !== null;
    const isAvorReadOnly = ! accessCtrl.canEditAvorProject;
    const isAvorAmountRequired = !isUpdate && accessCtrl.canEditAvorProject;

    const {handleAction, currentReturnTo} = useAppCtx();
    const {crew, lazyloadCrews} = useCrewCtx();

    const [formValues, setFormValues] = useState(isUpdate ? Project.mapDbToForm(currentData) : Project.getFormValues());
    const [formErrors, setFormErrors] = useState(Project.getFormErrors());
    const [openConfirm, setOpenConfirm] = useState(false);
    const [call, setCall] = useState(initialCall())

    useEffect(() => {
        lazyloadCrews();
    }, []);

    //
    // handers
    //
    const handleChange = (fieldOrEvent, value) => {
        if (typeof fieldOrEvent === "string") {
            // For DatePicker
            setFormValues((prev) => ({ ...prev, [fieldOrEvent]: DateUtil.convertDateToIsoString(value) }));
            setFormErrors((prev) => ({ ...prev, [fieldOrEvent]: false }));
        } else {
            // For TextField or CheckBox
            const { name, value, type, checked} = fieldOrEvent.target;
            if (type === "checkbox") {
                // For CheckBox: Convert checked state to 0/1
                setFormValues((prev) => ({ ...prev, [name]: checked ? 1 : 0 }));
            } else {
                // For TextField: Sanitize and update value
                setFormValues((prev) => ({ ...prev, [name]: value }));
            }
            setFormErrors((prev) => ({ ...prev, [name]: false }));
        }
    };

    const handleComboxChange = (field, value) => {
        setFormValues((prevValues) => ({
            ...prevValues,
            [field]: value
        }));
    }

    const handleSubmit = async (action) => {
        if (action === "cancel") {
            if (isUpdate) {
                if (currentReturnTo) {
                    handleAction(currentReturnTo);
                } else {
                    handleAction("projectDetail", currentData)
                }
            } else {
                handleAction("project");
            }
        }

        if (action === "delete") {
            setOpenConfirm(true)
            return
        }

        if (action === "save") {
            // validate
            const errorsCopy = { ...formErrors }; // Create a copy of the errors object
            const isValid = validate(formValues, errorsCopy);
            if (!isValid) {
                setFormErrors(errorsCopy); // Update errors in state
                return
            }
            // save (create new or update existing entity)
            setCall(runningCall);
            const responseCall = await projectSave(Project.mapFormToDb(formValues))
            if (responseCall.isError) {
                setCall(responseCall)
                return
            }
            // return to
            if (currentReturnTo) {
                handleAction(currentReturnTo);
            } else {
                handleAction("projectDetail", Project.mapDbToForm(responseCall.result))
            }
        }
    };

    const handleConfirm = async (action) => {
        setOpenConfirm(false);
        if (action === "yes") {
            setCall(runningCall);
            const responseCall = await projectDelete(currentData.id);
            if (responseCall.isError) {
                setCall(responseCall)
            } else {
                handleAction("project")
            }
        }
    }

    //
    // validation
    //
    function validate(formValues, formErrors) {
        let isValid = true;

        // remove leading and trailing blanks on all input fields
        ValidationUtil.trimTextFields(formValues)

        // validate required fields
        let requiredFields = [...Project.getFormRequiredFields()];
        if (isAvorAmountRequired) {
            requiredFields.push("avor_amount");
        }

        isValid = ValidationUtil.required(formValues, formErrors, requiredFields);

        // validate foreman_crew_id not equal deputy_foreman_crew_id
        if ( (formValues.foreman_crew_id !== -1) &&
             (formValues.deputy_foreman_crew_id !== -1) &&
             (formValues.foreman_crew_id === formValues.deputy_foreman_crew_id) ) {

            formErrors.deputy_foreman_crew_id = "Stellvertreter darf nicht identisch mit dem Bauführer sein.";
            isValid = false;
        }

        // return final validation status
        return isValid;
    }

    //
    // form inputs
    //
    const formTextField = (field, label, maxLength, numericOnly=false, readOnly=false) => {
        return <CxTextField field={field} label={label} maxLength={maxLength} numericOnly={numericOnly} readOnly={readOnly}
                            handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formDatePicker = (field, label, readOnly=false) => {
        return <CxDatePicker field={field} label={label} disablePast={false} readOnly={readOnly}
                             handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCombox = (field, label, items, readOnly=false) => {
        return <CxComboxSelection id={field} name={label} value={formValues[field]} readOnly={readOnly}
                                  onChange={(value) => handleComboxChange(field, value)}
                                  items={items} formErrors={formErrors} />
    }

    const formCheckBox = (field, label) => {
        return <CxCheckBox field={field} label={label} readOnly={false}
                           handleChange={handleChange} formValues={formValues} formErrors={formErrors} />
    }

    const formCreateButtons = () => {
        return <CxCancelSaveButton handleSubmit={handleSubmit} />
    }

    const formUpdateButtons = () => {
        return <CxDeleteCancelSaveButton  handleSubmit={handleSubmit} />
    }

    //
    // return form content
    //
    const avorAmountLabel = isAvorAmountRequired ? "Auftragssumme *" : "Auftragssumme";

    return (
        <CxLocalization>

            <CxFormFeedback call={call} />

            <CxGrid size="lg">{ formTextField("project_ref", "Projekt Nr *", 64) }</CxGrid>
            <CxGrid size="lg">{ formTextField("project_desc", "Projekt Beschreibung *", 1024) }</CxGrid>

            <CxGrid size="lg">{ formTextField("architect", "Auftraggeber *", 64) }</CxGrid>
            <CxGrid size="lg">{ formDatePicker("initial_date", "Projektstart *")}</CxGrid>

            <CxGrid size="lg">{ formTextField("address", "Adresse *", 64) }</CxGrid>
            <CxGrid size="sm">{ formTextField("zip", "PLZ *", 32) }</CxGrid>
            <CxGrid size="sm">{ formTextField("city", "Ort *", 32) }</CxGrid>

            <CxGrid size="lg">{ formTextField("sm_name", "Bauleiter/Ansprechperson vor Ort", 64) }</CxGrid>
            <CxGrid size="sm">{ formTextField("sm_phone", "Bauleiter Telefon", 32) }</CxGrid>
            <CxGrid size="sm">{ formTextField("sm_email", "Bauleiter E-Mail", 128) }</CxGrid>

            <CxGrid size="sm">{ formTextField("avor_amount", avorAmountLabel, 8, true, isAvorReadOnly) }</CxGrid>
            <CxGrid size="sm">{ formCombox("avor_state", "Vorausmass", Project.getAvorStateItems(), isAvorReadOnly) }</CxGrid>
            <CxGrid size="sm">{ formCombox("avor_created_crew_id", "Vorausmass erstellt von", crew.getAvorSelection(true), isAvorReadOnly) }</CxGrid>
            <CxGrid size="sm">{ formDatePicker("avor_created_at", "Vorausmass erstellt am", isAvorReadOnly) }</CxGrid>

            <CxGrid size="sm">{ formCombox("foreman_crew_id", "Bauführer", crew.getForemanSelection(true)) }</CxGrid>
            <CxGrid size="sm">{ formCombox("deputy_foreman_crew_id", "Bauführer Stellvertreter", crew.getForemanSelection(true)) }</CxGrid>
            <CxGrid size="sm" valign="middle">{ formCheckBox("closed", "Abgeschlossen") }</CxGrid>
            <CxGrid size="sm" />

            <CxGrid size="xl">{ formTextField("prep_info", i18n.prepInfo, 2048) }</CxGrid>

            <CxGrid size="xs">{ formCombox("prep_1", i18n.prep1, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_2", i18n.prep2, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_3", i18n.prep3, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_4", i18n.prep4, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_5", i18n.prep5, Project.getPrepStateItems()) }</CxGrid>
            <CxGrid size="xs">{ formCombox("prep_6", i18n.prep6, Project.getPrepStateItems()) }</CxGrid>

            <CxGrid size="xl" align="right">
                {isUpdate && accessCtrl.canDeleteProject ? formUpdateButtons() : formCreateButtons()}
            </CxGrid>

            <CxConfirmDialog
                open={openConfirm}
                title="Projekt löschen"
                text="Soll das Projekt mit allen Arbeitsvorgängen gelöscht werden?"
                handleConfirm={handleConfirm}
                severity="Delete" />

            <CxMetaInfo values={formValues} />

        </CxLocalization>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// Create, Update and Delete
// --------------------------------------------------------------------------------------------------------------------

export function ProjectCreate() {

    const renderHeader = () => {
        const title = "Projekt erstellen";
        return <PageHeader title={title} />
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { renderHeader() }
                { RenderProject() }
            </CxGrid>
        </Box>
    );
}


export function ProjectUpdate() {
    const { currentData } = useAppCtx();

    const renderHeader = () => {
        const title = "Projekt bearbeiten";
        return <PageHeader title={title} />
    }

    return (
        <Box sx={{ width: '100%' }}>
            <CxGrid container spacing={2}>
                { renderHeader() }
                { RenderProject(currentData) }
            </CxGrid>
        </Box>
    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.